import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface CompanyProduct {
  id: number;
  created_at: string;
  company_id: number;
  name: string;
  ean: number | null; // Should be max 13 digits
  hs_code: number | null;
  pieces_per_carton: number | null;
  weight_per_carton: number | null;
  carton_length: number | null;
  carton_width: number | null;
  carton_height: number | null;
  unit_price: number | null;
  picqer_product_id: number | null; // ID from Picqer system (idproduct)
  channeldock_product_id: number | null; // ID from ChannelDock seller inventory
}

export interface CompanyProductInput {
  company_id: number;
  name: string;
  ean: number | null; // Should be max 13 digits
  hs_code: number | null;
  pieces_per_carton: number | null;
  weight_per_carton: number | null;
  carton_length: number | null;
  carton_width: number | null;
  carton_height: number | null;
  unit_price: number | null;
  picqer_product_id?: number | null; // ID from Picqer system (idproduct)
  channeldock_product_id?: number | null; // ID from ChannelDock seller inventory
}

export const useCompanyProducts = (companyId?: number | null) => {
  const [products, setProducts] = useState<CompanyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!companyId) {
        setLoading(false);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('company_product')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setProducts(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch company products');
        console.error('Error fetching company products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [companyId, refreshTrigger]);

  const refresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const saveCompanyProduct = async (product: CompanyProductInput) => {
    try {
      // Validate and normalize data before insert
      const normalizedProduct = {
        ...product,
        // Ensure ean is a number or null
        ean: product.ean ? Number(product.ean) : null,
        // Ensure hs_code is a number or null
        hs_code: product.hs_code ? Number(product.hs_code) : null,
        // Ensure numeric fields are numbers or null
        pieces_per_carton: product.pieces_per_carton ? Number(product.pieces_per_carton) : null,
        weight_per_carton: product.weight_per_carton ? Number(product.weight_per_carton) : null,
        carton_length: product.carton_length ? Number(product.carton_length) : null,
        carton_width: product.carton_width ? Number(product.carton_width) : null,
        carton_height: product.carton_height ? Number(product.carton_height) : null,
        unit_price: product.unit_price ? Number(product.unit_price) : null,
      };

      const { data, error } = await supabase
        .from('company_product')
        .insert(normalizedProduct)
        .select()
        .single();

      if (error) {
        console.error('Error saving company product - Full error:', {
          error,
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          product: normalizedProduct,
        });
        throw error;
      }
      setProducts(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error('Error saving company product:', err);
      throw err;
    }
  };

  const updateCompanyProduct = async (id: number, updates: Partial<CompanyProductInput>) => {
    try {
      const { data, error } = await supabase
        .from('company_product')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setProducts(prev => 
        prev.map(product => product.id === id ? data : product)
      );
      
      return data;
    } catch (err) {
      console.error('Error updating company product:', err);
      throw err;
    }
  };

  const deleteCompanyProduct = async (id: number) => {
    try {
      const { error } = await supabase
        .from('company_product')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setProducts(prev => prev.filter(product => product.id !== id));
      
      return true;
    } catch (err) {
      console.error('Error deleting company product:', err);
      throw err;
    }
  };

  return { 
    products, 
    loading, 
    error, 
    saveCompanyProduct, 
    updateCompanyProduct,
    deleteCompanyProduct,
    refresh
  };
}; 